import api from '../api';
import {
  IMyInstituteReqBody,
  IOrganization,
  IOrganizationReqBody,
  IOrganizationResponse,
} from '../../interface/organizationModel';
import { AxiosResponse } from 'axios';
import { OrganizationPreferenceRequest } from '../../interface/organizationPreference.interface';
import {
  IOrganizationSecurityConfiguration,
  IOrganizationStudentAttendanceConfiguration,
  IOrganizationGeoAttendanceConfiguration,
} from '../../interface/profile.interface';

export const getOrganizationPreferences = async () => {
  const response = await api.get('/organization/preference');
  return response.data;
};

export const updateOrganizationPreferences = async (preferences: OrganizationPreferenceRequest) => {
  const response = await api.put('/organization/preference', preferences);
  return response.data;
};

/**
 * CVA-3914: read the institute's web 2FA mandate plus who last changed it.
 *
 * Readable by any authenticated member — the personal settings row needs it to
 * explain why web logins are challenged while the personal toggle is off.
 */
export const getOrganizationSecurityConfiguration = async (): Promise<
  AxiosResponse<{ data: IOrganizationSecurityConfiguration }>
> => {
  return api.get('/organization/security-configuration');
};

/**
 * CVA-3914: enable or disable the institute-wide web 2FA mandate.
 *
 * Requires the `Organization Configuration Management` privilege. The
 * organization is taken from the session on the server, never sent from here.
 */
export const updateOrganizationSecurityConfiguration = async (
  web2faEnforced: boolean,
): Promise<AxiosResponse<{ data: IOrganizationSecurityConfiguration }>> => {
  return api.put('/organization/security-configuration', { web2faEnforced });
};

/**
 * Read whether automated daily student attendance is enabled for the institute.
 *
 * No privilege required — the attendance screen uses it to explain an empty
 * register, and that screen is open to everyone who can mark attendance.
 */
export const getOrganizationStudentAttendanceConfiguration = async (): Promise<
  AxiosResponse<{ data: IOrganizationStudentAttendanceConfiguration }>
> => {
  return api.get('/organization/student-attendance-configuration');
};

/**
 * Pause or resume automated daily student attendance for the institute.
 *
 * Requires the `Organization Configuration Management` privilege. The
 * organization is taken from the session on the server, never sent from here.
 * Applies from the next hourly cron re-sync, not immediately.
 */
export const updateOrganizationStudentAttendanceConfiguration = async (
  dailyStudentAttendanceEnabled: boolean,
): Promise<AxiosResponse<{ data: IOrganizationStudentAttendanceConfiguration }>> => {
  return api.put('/organization/student-attendance-configuration', {
    dailyStudentAttendanceEnabled,
  });
};

/** CVA-335/336: read the institute's location-based attendance configuration. */
export const getOrganizationGeoAttendanceConfiguration = async (): Promise<
  AxiosResponse<{ data: IOrganizationGeoAttendanceConfiguration }>
> => {
  return api.get('/organization/geo-attendance-configuration');
};

/**
 * CVA-335/336: enable or disable location-based attendance, and set the radius.
 *
 * Enabling without a usable institute location is refused by the server with
 * `ORGANIZATION_GEO_LOCATION_NOT_SET`; the caller turns that into a prompt to
 * set the address rather than a bare error toast.
 */
export const updateOrganizationGeoAttendanceConfiguration = async (
  enabled: boolean,
  radiusMeters?: number,
): Promise<AxiosResponse<{ data: IOrganizationGeoAttendanceConfiguration }>> => {
  return api.put('/organization/geo-attendance-configuration', {
    enabled,
    ...(radiusMeters !== undefined && { radiusMeters }),
  });
};

export const fetchMyOrganization = async (): Promise<AxiosResponse<IOrganizationResponse>> => {
  const res: AxiosResponse<IOrganizationResponse> = await api.get('/me/organization');
  return res;
};

export const updateMyInstitute = async (body: IMyInstituteReqBody, id: string) => {
  const res = await api.put(`/organization/${id}`, body);
  return res;
};

export const fetchTeacherStudentCountsService = async () => {
  const response = await api.get(`/organization/student/teacher`);
  return response.data;
};

export const fetchCurrenciesService = async () => {
  const response = await api.get('/currencies');
  return response.data;
};

export const createOrganizationService = async (body: IOrganizationReqBody) => {
  const response = await api.post(`/organization/create`, body);
  return response.data;
};

export const updateOrganizationService = async (id: string, body: IOrganization) => {
  const response = await api.put(`/organization/update/${encodeURIComponent(id)}`, body);
  return response.data;
};

export const organizationService = {
  fetchMyOrganization,
  updateMyInstitute,
};
